import type { WorkflowDefinition } from './api'

/** Who a level names (ADR-0034). Mirrors the API's `approver_type`. */
export type ApproverType = 'user' | 'role' | 'system_role' | 'direct_manager'

/** How a path decides: every level in turn, or one level that can send it up. */
export type WorkflowMode = 'chain' | 'escalation'

export type WorkflowStepDraft = {
  clientKey: string
  approver_type: ApproverType | null
  approver_role_id: string | null
  approver_user_id: string | null
  approver_system_role: string | null
  /** Hours as typed; empty means "only by hand". */
  escalate_after_hours: string
}

type StepResource = WorkflowDefinition['steps'][number]

export const emptyStep = (clientKey: string): WorkflowStepDraft => ({
  clientKey,
  approver_type: null,
  approver_role_id: null,
  approver_user_id: null,
  approver_system_role: null,
  escalate_after_hours: '',
})

export function stepFromResource(step: StepResource): WorkflowStepDraft {
  return {
    clientKey: step.id,
    approver_type: step.approver_type as ApproverType,
    approver_role_id: step.approver_role_id ?? null,
    approver_user_id: step.approver_user_id ?? null,
    approver_system_role: step.approver_system_role ?? null,
    escalate_after_hours:
      step.escalate_after_hours === null || step.escalate_after_hours === undefined
        ? ''
        : String(step.escalate_after_hours),
  }
}

/**
 * One picker value per level: `manager`, `system:<key>`, `role:<id>` or
 * `user:<id>`. One picker keeps choosing a level to one decision.
 */
export function pickerValue(step: WorkflowStepDraft): string {
  switch (step.approver_type) {
    case 'direct_manager':
      return 'manager'
    case 'system_role':
      return `system:${step.approver_system_role}`
    case 'role':
      return `role:${step.approver_role_id}`
    case 'user':
      return `user:${step.approver_user_id}`
    default:
      return ''
  }
}

export function applyPicker(step: WorkflowStepDraft, value: string): WorkflowStepDraft {
  const cleared = {
    ...step,
    approver_role_id: null,
    approver_user_id: null,
    approver_system_role: null,
  }
  if (value === 'manager') return { ...cleared, approver_type: 'direct_manager' }
  const [kind, id = ''] = [value.slice(0, value.indexOf(':')), value.slice(value.indexOf(':') + 1)]
  if (kind === 'system')
    return { ...cleared, approver_type: 'system_role', approver_system_role: id }
  if (kind === 'role') return { ...cleared, approver_type: 'role', approver_role_id: id }
  if (kind === 'user') return { ...cleared, approver_type: 'user', approver_user_id: id }
  return { ...cleared, approver_type: null }
}

export const isChosen = (step: WorkflowStepDraft): boolean => step.approver_type !== null

/** A delay is a whole number of hours from 1 to 720 (the API's limit), or empty. */
export function delayIsValid(value: string): boolean {
  if (value.trim() === '') return true
  const hours = Number(value)
  return Number.isInteger(hours) && hours >= 1 && hours <= 720
}

/**
 * The API shape of each level. A delay is sent only in escalation mode and
 * never on the last level, which has no level above it.
 */
export function stepsToInput(steps: WorkflowStepDraft[], mode: WorkflowMode) {
  return steps.map((step, index) => {
    const delay = step.escalate_after_hours.trim()
    const canDelay = mode === 'escalation' && index < steps.length - 1
    return {
      approver_type: step.approver_type,
      approver_role_id: step.approver_role_id,
      approver_user_id: step.approver_user_id,
      approver_system_role: step.approver_system_role,
      escalate_after_hours: canDelay && delay !== '' ? Number(delay) : null,
    }
  })
}
